import { useState } from "react";
import { useScheme } from "@certn/cascadia-react";
import { darkColors, lightColors } from "@certn/cascadia-tokens";
import { useSavePrimaryColor } from "./useSavePrimaryColor";
import { useFetchCobranding } from "./useFetchCobranding";
import { getTextColorForBackground } from "@certn/cascadia-react/utils/colorValidation";
interface UseColorFormOptions {
  onColorChange: (color: string) => void;
}

export function useColorForm({ onColorChange }: UseColorFormOptions) {
  const { primaryColor, isLoading: isFetchingColor } = useFetchCobranding();
  const scheme = useScheme();
  const { savePrimaryColor, isSubmitting } = useSavePrimaryColor();
  const [applyChanges, setApplyChanges] = useState(false);
  const defaultColor =
    scheme === "light"
      ? lightColors["background-brand-primary-strong"]
      : darkColors["background-brand-primary-strong"];

  const initialValues = {
    primaryColor: primaryColor || defaultColor,
  };

  const handleSubmit = async (values: { primaryColor: string }) => {
    const optimalTextColor = getTextColorForBackground(values.primaryColor);
    const result = await savePrimaryColor(
      values.primaryColor,
      optimalTextColor
    );
    // Only reset applyChanges if save was successful
    // (result will be undefined on failure since submit doesn't throw by default)
    // Cache invalidation handled by useSavePrimaryColor
    if (result !== undefined) {
      onColorChange(values.primaryColor);
      setApplyChanges(false);
    }
  };

  const handleReset = (
    resetForm: () => void,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const defaultColor =
      scheme === "light"
        ? lightColors["background-brand-primary-strong"]
        : darkColors["background-brand-primary-strong"];
    resetForm();
    setFieldValue("primaryColor", defaultColor as string);
    onColorChange(defaultColor as string);
    setApplyChanges(true);
  };

  const handleColorChange = (
    color: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("primaryColor", color);
    onColorChange(color);
    setApplyChanges(true);
  };

  const handleCancel = (setFieldValue: (field: string, value: any) => void) => {
    const fallbackColor = primaryColor || defaultColor;
    setFieldValue("primaryColor", fallbackColor);
    onColorChange(fallbackColor);
    setApplyChanges(false);
  };

  return {
    initialValues,
    primaryColor,
    isSubmitting,
    isFetchingColor,
    applyChanges,
    handleSubmit,
    handleReset,
    handleColorChange,
    handleCancel,
  };
}
